import Styles from './styles.module.css';
import '../../auth/login/tokens.css';
import React, { useEffect, useState } from 'react';
import { doc, getDoc, getFirestore, updateDoc } from 'firebase/firestore';
import {
  IoPersonOutline, IoPencil, IoArrowUp, IoArrowDown, IoTrashOutline,
} from 'react-icons/io5';
import { useAppSelector } from 'src/salon/app/hook';
import ContactModal from './ContactModal';
import CalendarPanel from './CalendarPanel';
import {
  Availability, ContactData, FlowStep,
  emptyContact, emptyStep, MAX_STEPS, photoUrl, emptyAvailability, normalizeAvailability,
} from './shared';

// 「サロン見学情報」ページ。求人・情報発信ナビの1項目。
// レイアウトは3分割：上=担当者(モーダルで編集) / 左下=当日のスケジュール / 右下=見学の受付枠(カレンダー)。
// テイストは他のサロン管理画面（10.salonPr / 15.evaluation）と同じ --mw-* トークン準拠。

const VisitInfo: React.FC = () => {

  const company = useAppSelector((state) => state.company.company);
  const companyId = company?.id ?? '';
  const recruitId = companyId ? `${companyId}_2027` : '';

  const [ loading, setLoading ] = useState(true);
  const [ savingFlow, setSavingFlow ] = useState(false);
  const [ savedFlow, setSavedFlow ] = useState(false);
  const [ flowError, setFlowError ] = useState('');
  // バリデーションで弾かれた「入力途中」ステップの行番号（0始まり）。枠を赤くする。
  const [ flowErrorRows, setFlowErrorRows ] = useState<number[]>([]);

  const [ contact, setContact ] = useState<ContactData>(emptyContact);
  const [ modalOpen, setModalOpen ] = useState(false);

  const [ steps, setSteps ] = useState<FlowStep[]>([]);

  // ③ 見学の受付枠。読み込み後に CalendarPanel へ初期値として渡す（以降の保存はパネル側で完結）。
  const [ availability, setAvailability ] = useState<Availability>(emptyAvailability);

  useEffect(() => {
    if (!recruitId) return;
    (async () => {
      setLoading(true);
      const db = getFirestore();
      const snap = await getDoc(doc(db, 'recruits', recruitId));
      const vi = snap.data()?.visitInfo;

      setContact({
        name: vi?.contact?.name ?? '',
        role: vi?.contact?.role ?? '',
        message: vi?.contact?.message ?? '',
        photoUpdatedAt: vi?.contact?.photoUpdatedAt ?? 0,
      });

      const flow = Array.isArray(vi?.flow) ? vi.flow : [];
      setSteps(
        flow.length > 0
          ? flow.map((s: any) => ({
            label: s?.label ?? '',
            durationMin: s?.durationMin != null ? String(s.durationMin) : '',
            description: s?.description ?? '',
          }))
          : [emptyStep()]
      );

      setAvailability(normalizeAvailability(vi?.availability));

      setLoading(false);
    })();
  }, [recruitId]);

  // ステップを編集したらエラー表示は一旦リセットする。
  const clearFlowError = () => {
    setFlowError('');
    setFlowErrorRows([]);
  };

  const updateStep = (index: number, key: keyof FlowStep, value: string) => {
    clearFlowError();
    setSteps((prev) => prev.map((s, i) => (i === index ? { ...s, [key]: value } : s)));
  };
  const addStep = () => {
    clearFlowError();
    setSteps((prev) => (prev.length >= MAX_STEPS ? prev : [...prev, emptyStep()]));
  };
  const removeStep = (index: number) => {
    clearFlowError();
    setSteps((prev) => prev.filter((_, i) => i !== index));
  };
  const moveStep = (index: number, dir: -1 | 1) => {
    clearFlowError();
    setSteps((prev) => {
      const next = [...prev];
      const target = index + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const totalMin = steps.reduce((sum, s) => sum + (Number(s.durationMin) || 0), 0);

  // ステップの入力状態を判定する。
  // filled: 何か1つでも入っている（＝入力途中の行）
  // complete: 内容・所要時間(正の数)・説明 が全てそろっている
  const stepFilled = (s: FlowStep) =>
    s.label.trim() !== '' || s.durationMin.trim() !== '' || s.description.trim() !== '';
  const stepComplete = (s: FlowStep) =>
    s.label.trim() !== '' && Number(s.durationMin) > 0 && s.description.trim() !== '';

  // 指定行の特定フィールドが未入力/不正か（赤枠の判定用）。エラー行のときだけ有効。
  const fieldHasError = (index: number, key: keyof FlowStep) => {
    if (!flowErrorRows.includes(index)) return false;
    const s = steps[index];
    if (!s) return false;
    if (key === 'durationMin') return !(Number(s.durationMin) > 0);
    return s[key].trim() === '';
  };

  async function saveFlow() {
    if (!recruitId) return;
    setFlowError('');
    setFlowErrorRows([]);

    // 入力途中（何か入っているが全項目そろっていない）の行を集める。
    const incompleteRows = steps
      .map((_, i) => i)
      .filter((i) => stepFilled(steps[i]) && !stepComplete(steps[i]));

    if (incompleteRows.length > 0) {
      setFlowErrorRows(incompleteRows);
      setFlowError(
        `ステップ${incompleteRows.map((i) => i + 1).join('・')}は「内容・所要時間・説明」をすべて入力してください。`
      );
      return;
    }

    const cleaned = steps
      .filter(stepComplete)
      .map((s) => ({
        label: s.label.trim(),
        durationMin: Number(s.durationMin),
        description: s.description.trim(),
      }));

    if (cleaned.length === 0) {
      setFlowError('見学のステップを1つ以上、「内容・所要時間・説明」まで入力してください。');
      return;
    }

    setSavingFlow(true);
    try {
      const db = getFirestore();
      await updateDoc(doc(db, 'recruits', recruitId), { 'visitInfo.flow': cleaned });

      setSteps(cleaned.map((s) => ({
        label: s.label,
        durationMin: String(s.durationMin),
        description: s.description,
      })));
      setSavedFlow(true);
      setTimeout(() => setSavedFlow(false), 2000);
    } catch (error) {
      console.error('当日のスケジュールの保存に失敗しました', error);
      setFlowError('保存に失敗しました。時間をおいて再度お試しください。');
    } finally {
      setSavingFlow(false);
    }
  }

  const hasContact = contact.name.trim() !== '' || contact.photoUpdatedAt > 0;
  const contactPhoto = contact.photoUpdatedAt > 0 ? photoUrl(companyId, contact.photoUpdatedAt) : '';

  if (!recruitId) {
    return <p className={Styles.loading}>求人情報が見つかりません。</p>;
  }
  if (loading) {
    return <p className={Styles.loading}>読み込み中...</p>;
  }

  return (
    <div className={Styles.page}>

      {/* 上：担当者（モーダルで編集） */}
      <section className={Styles.panel}>
        {hasContact ? (
          <div className={Styles.contactSummary}>
            {contactPhoto
              ? <img className={Styles.contactPhoto} src={contactPhoto} alt="担当者の顔写真" />
              : <span className={Styles.contactPhoto}><IoPersonOutline /></span>}
            <div className={Styles.contactText}>
              <p className={Styles.contactMsg}>{contact.message || 'ひとこと未設定'}</p>
              <p className={Styles.contactName}>
                {contact.name || '担当者名'}{contact.role ? `（${contact.role}）` : ''}
              </p>
            </div>
            <button type="button" className={Styles.editBtn} onClick={() => setModalOpen(true)}>
              <IoPencil />
              編集
            </button>
          </div>
        ) : (
          <div className={Styles.contactEmpty}>
            <div className={Styles.contactEmptyText}>
              <p className={Styles.contactEmptyTitle}>見学の担当者が未設定です</p>
              <p className={Styles.contactEmptyDesc}>
                顔写真とひとことを登録すると、アプリの見学申込ボタン付近に吹き出しで表示されます。
              </p>
            </div>
            <button type="button" className={Styles.primaryBtn} onClick={() => setModalOpen(true)}>
              担当者を設定する
            </button>
          </div>
        )}
      </section>

      <div className={Styles.panels}>

        {/* 左下：当日のスケジュール */}
        <section className={Styles.panel}>
          <div className={Styles.panelHead}>
            <h3 className={Styles.panelTitle}>当日のスケジュール</h3>
            <p className={Styles.panelDesc}>見学で何をするか・所要時間をステップで登録します。</p>
          </div>

          <div className={Styles.stepList}>
            {steps.map((step, i) => (
              <div
                className={flowErrorRows.includes(i) ? `${Styles.stepItem} ${Styles.stepItemError}` : Styles.stepItem}
                key={i}
              >
                <span className={Styles.stepIndex}>{i + 1}</span>

                <div className={Styles.stepBody}>
                  <div className={Styles.stepTopRow}>
                    <input
                      className={`${Styles.input} ${Styles.stepLabelInput} ${fieldHasError(i, 'label') ? Styles.inputError : ''}`}
                      placeholder="店舗案内"
                      maxLength={30}
                      value={step.label}
                      onChange={(e) => updateStep(i, 'label', e.target.value)}
                      disabled={savingFlow}
                    />
                    <input
                      className={`${Styles.input} ${Styles.stepDurationInput} ${fieldHasError(i, 'durationMin') ? Styles.inputError : ''}`}
                      placeholder="分"
                      inputMode="numeric"
                      maxLength={3}
                      value={step.durationMin}
                      onChange={(e) => updateStep(i, 'durationMin', e.target.value.replace(/[^0-9]/g, ''))}
                      disabled={savingFlow}
                    />
                  </div>
                  <textarea
                    className={`${Styles.input} ${Styles.textarea} ${fieldHasError(i, 'description') ? Styles.inputError : ''}`}
                    placeholder="店内をまわりながら、雰囲気・スタッフ・設備をご案内します。"
                    maxLength={200}
                    value={step.description}
                    onChange={(e) => updateStep(i, 'description', e.target.value)}
                    disabled={savingFlow}
                  />
                </div>

                <div className={Styles.stepControls}>
                  <button
                    type="button"
                    className={Styles.iconBtn}
                    onClick={() => moveStep(i, -1)}
                    disabled={savingFlow || i === 0}
                    aria-label="ひとつ上へ"
                  >
                    <IoArrowUp />
                  </button>
                  <button
                    type="button"
                    className={Styles.iconBtn}
                    onClick={() => moveStep(i, 1)}
                    disabled={savingFlow || i === steps.length - 1}
                    aria-label="ひとつ下へ"
                  >
                    <IoArrowDown />
                  </button>
                  <button
                    type="button"
                    className={`${Styles.iconBtn} ${Styles.iconBtnDanger}`}
                    onClick={() => removeStep(i)}
                    disabled={savingFlow}
                    aria-label="このステップを削除"
                  >
                    <IoTrashOutline />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <button
            type="button"
            className={Styles.addStepBtn}
            onClick={addStep}
            disabled={savingFlow || steps.length >= MAX_STEPS}
          >
            ＋ ステップを追加する
          </button>

          {totalMin > 0 && (
            <div className={Styles.totalRow}>所要時間 約{totalMin}分</div>
          )}

          <div className={Styles.panelSaveRow}>
            {flowError && <p className={Styles.saveError}>{flowError}</p>}
            {savedFlow && <p className={Styles.saveHint}>保存しました。</p>}
            <button type="button" className={Styles.saveButton} onClick={saveFlow} disabled={savingFlow}>
              {savingFlow ? '保存中...' : '保存する'}
            </button>
          </div>
        </section>

        {/* 右下：見学の受付枠（カレンダー） */}
        <CalendarPanel recruitId={recruitId} initial={availability} />
      </div>

      {modalOpen && (
        <ContactModal
          companyId={companyId}
          recruitId={recruitId}
          initial={contact}
          onClose={() => setModalOpen(false)}
          onSaved={(next) => setContact(next)}
        />
      )}
    </div>
  );
};

export default VisitInfo;
